/**
 * FailedTakeover — replaces the Overview body when provisioning failed.
 *
 * Surfaces three things so the failure is self-explaining without a manual log
 * dig: the failing STEP (e.g. "Phase 7 · localhost /health" / "Deploy ·
 * app-up"), the one-line termination reason, and — collapsed — the captured
 * service diagnostics (compose `ps` + the crashing container's logs / deploy.log
 * tail). The step + diagnostics come from the failing `health_check_results`
 * entry, which the provision job writes for every failure path.
 */
import React from 'react';
import { Button, Card, Heading, Text } from '@chatsdet/ui';
import { Lifebuoy, WarningCircle } from '@phosphor-icons/react';
import type { HealthCheckResult } from '../../../hooks/useEnvironmentInstances';
import { getSandboxFailureDiagnostics } from './sandboxFailure';
import { SandboxExpiryCountdown } from './SandboxExpiryCountdown';

export interface FailedTakeoverProps {
  message?: string | null;
  /**
   * The instance's `healthCheckResults` — the canonical per-step failure record.
   * The last `passed: false` entry names the failing step and carries the full
   * diagnostics blob.
   */
  healthCheckResults?: HealthCheckResult[] | null;
  /**
   * When set, the EC2 is being held for debugging until this ISO timestamp
   * (sandbox.debug.preserveFailedInstance). Surfaces a "still SSM-able" note
   * with a live countdown. Superadmin-only (computed server-side).
   */
  preservedUntil?: string | null;
  onRegenerate: () => void;
  onViewLogs: () => void;
}

export function FailedTakeover({
  message,
  healthCheckResults,
  preservedUntil,
  onRegenerate,
  onViewLogs,
}: FailedTakeoverProps) {
  const { step, diagnostics } = getSandboxFailureDiagnostics(healthCheckResults);
  return (
    <div className="max-w-[640px] mx-auto">
      <Card padding="none" className="overflow-hidden border-red-200">
        <div className="p-5 border-b border-red-200 bg-red-50/60 flex items-center gap-3">
          <div className="h-9 w-9 rounded-md bg-red-100 flex items-center justify-center">
            <WarningCircle size={20} weight="fill" className="text-red-600" />
          </div>
          <div>
            <Heading level={4} className="text-[18px] text-red-800">
              Sandbox provisioning failed
            </Heading>
            <Text size="sm" color="muted">
              Something went wrong while bringing this sandbox up.
            </Text>
          </div>
        </div>
        <div className="p-5 flex flex-col gap-4">
          {preservedUntil ? (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50/70 p-3">
              <Lifebuoy size={16} weight="fill" className="mt-0.5 shrink-0 text-amber-600" />
              <Text size="sm" color="muted">
                This sandbox&apos;s EC2 is being held for debugging — you can still
                SSM in for{' '}
                <SandboxExpiryCountdown
                  expiresAt={preservedUntil}
                  variant="compact"
                  prefix={null}
                  className="font-medium"
                />{' '}
                before it&apos;s reaped.
              </Text>
            </div>
          ) : null}
          {step ? (
            <div className="flex items-baseline gap-2">
              <Text size="sm" weight="semibold" color="muted" className="shrink-0">
                Failed at
              </Text>
              <span className="text-xs font-mono rounded bg-red-50 text-red-700 border border-red-200 px-1.5 py-0.5">
                {step}
              </span>
            </div>
          ) : null}
          {message ? (
            <pre className="text-xs whitespace-pre-wrap break-words bg-slate-50 rounded-md p-3 border border-border max-h-[240px] overflow-auto">
              {message}
            </pre>
          ) : (
            <Text size="sm" color="muted">
              No diagnostic message captured. Try regenerating or inspect the logs.
            </Text>
          )}
          {diagnostics ? (
            <details className="group rounded-md border border-border bg-slate-50/60">
              <summary className="cursor-pointer select-none px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground">
                Service diagnostics (container status &amp; logs)
              </summary>
              <pre className="text-xs whitespace-pre-wrap break-words border-t border-border p-3 max-h-[360px] overflow-auto">
                {diagnostics}
              </pre>
            </details>
          ) : null}
          <div className="flex gap-2 flex-wrap">
            <Button variant="primary" onPress={onRegenerate}>
              Regenerate from template
            </Button>
            <Button variant="outline" onPress={onViewLogs}>
              View logs
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

FailedTakeover.displayName = 'FailedTakeover';
